import { ComponentType } from '@angular/cdk/overlay';
import { Injector, inject, makeEnvironmentProviders } from '@angular/core';
import { Router } from '@angular/router';

import { MemberProfilesService } from '@lib/members/data-access';
import { Prescription, PrescriptionsSearch, PrescriptionsService, clearPrescriptions, clearTargetPrescription } from '@lib/prescriptions/data-access';
import { CreateAppointmentModalComponent, CreateLaboratoryOrderModalComponent } from '@lib/prescriptions/ui';
import { Store } from '@ngrx/store';
import { firstValueFrom, map } from 'rxjs';

import { MatDialog } from '@angular/material/dialog';

import { selectUser } from '@hm/core/stores/hm/hm.selectors';

import { environment } from '@hm/environments/environment';

import { provideChatState } from '@lib/chat/core/providers/chat-state.provider';
import { ChatCommunicationService } from '@lib/chat/core/services/chat-communication.service';
import { ChatNavigationService } from '@lib/chat/core/services/chat-navigation.service';
import { CHAT_NAVIGATION_HANDLERS, type ChatNavigationHandlers } from '@lib/chat/core/tokens/chat-navigation.tokens';
import { CHAT_API_URL, CHAT_LIB_CONFIG } from '@lib/chat/core/tokens/chat.tokens';
import { ChatLibConfig, ThreadData } from '@lib/chat/models/chat-config.interface';
import { ThreadType } from '@lib/chat/models/chat.interface';

import { DEFAULT_PAGE_SIZE } from '@lib/commons/constants/request.const';

import { AppointmentEventPlaceType, CreateAppointmentModalType } from '@lib/core/models/create-appointment.interface';
import { MemberProfile } from '@lib/core/models/member.interface';
import { AppointmentTypes } from '@lib/core/models/visit.interface';

export function provideChatLibConfig() {
  return makeEnvironmentProviders([
    { provide: CHAT_API_URL, useValue: environment.protectedApiUrl },
    {
      provide: CHAT_LIB_CONFIG,
      useFactory: (): ChatLibConfig => {
        const injector = inject(Injector);

        return {
          getUserInfo: () => {
            const store = injector.get(Store);
            const user = store.selectSignal(selectUser)();

            return {
              userId: user?.userId || '00000000-0000-0000-0000-000000000000',
              senderName: user?.fullName || 'Менеджер здоров`я',
            };
          },
          excludeThreadTypes: [ThreadType.AiAssistant],
          features: {
            showThreadTabs: true,
            showMedicalHistory: true,
            showPrescriptionsTab: true,
            showMemberProfile: true,
            showHealthCheckScheduler: true,
            enableProfileNavigation: true,
            enableAppointmentCreation: true,
          },
          notificationConfig: {
            title: 'У вас нове повідомлення',
            icon: 'dist/assets/images/logo.svg',
            soundEnabled: true,
          },
          resolveThreadData: async (threadId: string) => {
            try {
              // Use captured injector
              const memberProfilesService = injector.get(MemberProfilesService);
              const chatApiService = injector.get(ChatCommunicationService);
              const prescriptionsService = injector.get(PrescriptionsService);

              const thread = await firstValueFrom(chatApiService.getThread(threadId));
              const prescriptionSearchPayload: PrescriptionsSearch = {
                page: 1,
                pageSize: DEFAULT_PAGE_SIZE,
                sortOrder: 'Descending',
                userId: thread.ownerId,
              };
              let memberProfileInfo: MemberProfile | null = null;
              let memberPrescriptions: Prescription[] = [];

              try {
                memberProfileInfo = await firstValueFrom(memberProfilesService.getClientProfileByUserId(thread.ownerId));

                if (memberProfileInfo) {
                  memberPrescriptions = await firstValueFrom(
                    prescriptionsService.POCPrescriptionsSearch(prescriptionSearchPayload).pipe(map(res => res.value))
                  );
                }
              } catch (memberError) {
                console.warn('Could not load member data, continuing with thread only:', memberError);
              }

              return {
                thread,
                profile: memberProfileInfo,
                relatedItems: memberPrescriptions,
              } as ThreadData;
            } catch (error) {
              console.error('Error resolving thread data:', error);
              throw error;
            }
          },
        };
      },
      deps: [],
    },
    {
      provide: CHAT_NAVIGATION_HANDLERS,
      useFactory: (
        router: Router,
        dialog: MatDialog,
        store: Store,
        memberProfilesService: MemberProfilesService,
        chatNavigationService: ChatNavigationService,
        prescriptionsService: PrescriptionsService
      ): ChatNavigationHandlers => ({
        navigateToPrescription: (prescriptionId: string, userId: string) => {
          memberProfilesService.getClientProfileByUserId(userId).subscribe(memberProfile => {
            store.dispatch(clearTargetPrescription());
            store.dispatch(clearPrescriptions());
            router.navigate([`/members/${memberProfile.id}/guarantees`], {
              queryParams: { prescription: prescriptionId },
            });
          });
        },

        switchToPrescriptionTab: (prescriptionId: string) => {
          chatNavigationService.switchToPrescriptionTab(prescriptionId);
        },

        openCreateAppointmentModal: async (userId: string, modalType: CreateAppointmentModalType, metadata?) => {
          // TODO: remove both fields handling after mobile will use prescriptionIds only
          // Handle both prescriptionIds (array) and prescriptionId (single) cases
          const prescriptionIds: string[] = metadata?.prescriptionIds || (metadata?.prescriptionId ? [metadata.prescriptionId] : []);
          const appointmentType: AppointmentTypes = metadata?.messageType === 'laboratory_request' ? AppointmentTypes.LaboratoryService : AppointmentTypes.Appointment;
          let prescriptions: Prescription[] = [];

          if (prescriptionIds && prescriptionIds.length > 0) {
            try {
              const prescriptionPromises = prescriptionIds.map(id => firstValueFrom(prescriptionsService.getPrescriptionById(id)));
              prescriptions = await Promise.all(prescriptionPromises);
            } catch (error) {
              console.error('Error fetching prescription data:', error);
            }
          }

          const prescriptionDetails = prescriptions.map(p => ({
            id: p.id,
            franchise: p.franchise ?? 0,
            name: p.name,
          }));

          const componentToOpen: ComponentType<CreateLaboratoryOrderModalComponent | CreateAppointmentModalComponent> =
            appointmentType === AppointmentTypes.LaboratoryService ? CreateLaboratoryOrderModalComponent : CreateAppointmentModalComponent;
          const modalData = {
            data: {
              userId: userId,
              type: modalType,
              appointmentType: appointmentType,
              appointmentName: metadata?.prescriptionName ?? prescriptions[0]?.name,
              eventPlace: AppointmentEventPlaceType.Hospital,
              prescriptions: prescriptionDetails,
              partnerId: metadata?.partnerId,
              locationId: metadata?.locationId,
            },
            width: '800px',
            maxHeight: '90vh',
          };

          dialog.open(componentToOpen, modalData);
        },

        navigateToMemberProfile: (userId: string) => {
          memberProfilesService.getClientProfileByUserId(userId).subscribe(memberProfile => {
            router.navigate([`/members/${memberProfile.id}/profile`]);
          });
        },
      }),
      deps: [Router, MatDialog, Store, MemberProfilesService, ChatNavigationService, PrescriptionsService],
    },
    provideChatState(),
  ]);
}
